import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { mergeCustomStyles } from 'app/concierge/tools/mergeCustomStyles';
import { Alert, Box, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import useBundleTranslation from 'i18n';
import IconMi from 'components/common/icon/IconMi';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { Popup } from 'components/common/popup/Popup';
import toast from 'app/toast';
import getErrorMessage from 'tools/errorMessage';
import { IRecentChat } from 'api/concierge';
import { useRecentChatMutations, useRecentChats } from 'app/concierge/hooks/useRecentChats';
import { groupByDate } from './groupByDate';
import RecentChatRow from './RecentChatRow';
import RenameChatPopup from './RenameChatPopup';
import { ICustomStyles } from '../index';
import defaultStyles from './styles';

const SEARCH_DELAY_MS = 250;
const LOAD_MORE_THRESHOLD_PX = 120;

export default function RecentChats({
    onOpenChat,
    onChatRenamed,
    onChatDeleted,
}: {
    onOpenChat: (chat: IRecentChat) => void;
    onChatRenamed: (chat: IRecentChat) => void;
    onChatDeleted: (conversationTs: string) => void;
}) {
    const { t, i18n } = useBundleTranslation(['app/concierge/widget']);
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');
    const [renaming, setRenaming] = useState<IRecentChat | null>(null);
    const [renameError, setRenameError] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<IRecentChat | null>(null);
    const { chats, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage, refetch } =
        useRecentChats(query);
    const { rename, pin, remove } = useRecentChatMutations();
    const customStyles = useSelector((state: any) => state.concierge.customStyles) as ICustomStyles;

    const styles = useMemo(() => {
        return mergeCustomStyles(defaultStyles, customStyles?.recentChats, 'container');
    }, [customStyles]);

    useEffect(() => {
        const timer = window.setTimeout(() => setQuery(search.trim()), SEARCH_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, [search]);

    // The app's queries do not refetch on mount; the list must, since chats change while the tab is away.
    useEffect(() => {
        refetch();
    }, []);

    // The next page loads near the bottom; scrolling there again retries a page that failed.
    const loadMoreNearBottom = (event: React.UIEvent<HTMLElement>) => {
        const list = event.currentTarget;
        const nearBottom = list.scrollHeight - list.scrollTop - list.clientHeight < LOAD_MORE_THRESHOLD_PX;
        if (nearBottom && hasNextPage && !isFetchingNextPage) fetchNextPage();
    };

    const failed = (error: unknown) => toast('error', getErrorMessage(error) || t('recent_chats.action_failed'));

    const rowProps = {
        query,
        onOpen: onOpenChat,
        onPin: (chat: IRecentChat) =>
            pin.mutate({ conversationTs: chat.conversation_ts, pinned: !chat.pinned }, { onError: failed }),
        onRename: (chat: IRecentChat) => {
            setRenameError(null);
            setRenaming(chat);
        },
        onDelete: setDeleting,
    };

    const searching = '' !== query;
    const pinned = chats.filter((chat) => chat.pinned);
    const recents = chats.filter((chat) => !chat.pinned);
    const group = (label: string, rows: IRecentChat[], testId: string) =>
        rows.length > 0 && (
            <Box key={testId} data-test={testId}>
                <Typography sx={styles.groupTitle}>{label}</Typography>
                {rows.map((chat) => (
                    <RecentChatRow key={chat.conversation_ts} chat={chat} {...rowProps} />
                ))}
            </Box>
        );

    return (
        <Stack sx={styles.container} data-test="concierge_recent_chats">
            <Box sx={styles.searchBar}>
                <TextField
                    fullWidth
                    size="small"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t('recent_chats.search_placeholder')}
                    inputProps={{ 'data-test': 'concierge_recent_chats_search' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <IconMi icon="search" fontSize="16" />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setSearch('');
                                        setQuery('');
                                    }}
                                    aria-label={t('recent_chats.clear_search')}
                                    data-test="concierge_recent_chats_search_clear"
                                    sx={{ p: '2px', color: 'primary.main' }}
                                >
                                    <IconMi icon="times" fontSize="16" />
                                </IconButton>
                            </InputAdornment>
                        ) : undefined,
                    }}
                />
            </Box>
            <Box sx={styles.chatList} onScroll={loadMoreNearBottom}>
                {isLoading && <LoadingPlaceholder />}
                {isError && 0 === chats.length && <Alert severity="error">{t('recent_chats.load_failed')}</Alert>}
                {!isLoading && !isError && !searching && 0 === chats.length && (
                    <Typography sx={styles.empty} data-test="concierge_recent_chats_empty">
                        {t('recent_chats.empty')}
                    </Typography>
                )}
                {searching ? (
                    <Box data-test="concierge_recent_chats_results">
                        {chats.map((chat) => (
                            <RecentChatRow key={chat.conversation_ts} chat={chat} {...rowProps} />
                        ))}
                    </Box>
                ) : (
                    <Stack spacing={1.5}>
                        {group(t('recent_chats.pinned'), pinned, 'concierge_recent_chats_pinned')}
                        {groupByDate(recents, new Date(), i18n?.language).map((dated) =>
                            group(
                                dated.labelKey ? t(dated.labelKey) : (dated.label ?? ''),
                                dated.chats,
                                `concierge_recent_chats_${dated.key}`,
                            ),
                        )}
                    </Stack>
                )}
                {isFetchingNextPage && <LoadingPlaceholder />}
            </Box>
            {renaming && (
                <RenameChatPopup
                    title={renaming.title}
                    saving={rename.isPending}
                    error={renameError}
                    onCancel={() => setRenaming(null)}
                    onSave={(title) =>
                        rename.mutate(
                            { conversationTs: renaming.conversation_ts, title },
                            {
                                onSuccess: (row) => {
                                    setRenaming(null);
                                    onChatRenamed(row);
                                },
                                onError: (error) =>
                                    setRenameError(getErrorMessage(error) || t('recent_chats.action_failed')),
                            },
                        )
                    }
                />
            )}
            {deleting && (
                <Popup
                    open
                    settings={{
                        title: t('recent_chats.delete.title'),
                        needTranslation: false,
                        textOK: t('recent_chats.delete.confirm'),
                        colorOk: 'error',
                        textCancel: t('recent_chats.delete.cancel'),
                        dataTestOkButton: 'concierge_delete_chat_confirm',
                        dataTestCancelButton: 'concierge_delete_chat_cancel',
                    }}
                    onHide={() => setDeleting(null)}
                    onConfirm={() =>
                        !remove.isPending &&
                        remove.mutate(deleting.conversation_ts, {
                            onSuccess: () => {
                                onChatDeleted(deleting.conversation_ts);
                                setDeleting(null);
                            },
                            onError: (error) => {
                                setDeleting(null);
                                failed(error);
                            },
                        })
                    }
                    okDisabled={remove.isPending}
                    maxWidth="popupSm"
                    sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
                >
                    <Typography data-test="concierge_delete_chat_message">
                        {t('recent_chats.delete.message')}
                    </Typography>
                </Popup>
            )}
        </Stack>
    );
}
